import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import MoreMenu, { COLORS, colorName, swatch } from './MoreMenu';
import { Icon, NoteGlyph } from './icons';
import { NoteImage, NoteVoice } from './NoteMedia';
import { reminderLabel } from './RepeatReminders';
import { audioOf, checklistPreview, imagesOf, lineId, MAX_LINES } from './notes';
import './TodoNotes.css';

/*
 * NOTES, Google Keep's way (to-dos, batch 5).
 *
 * Gordon: the last of the to-do work - "Google Keep-style notes (coloured
 * cards, checklists inside a note, photos and voice notes, pinning,
 * archive)". So the Notes view is Keep's grid: cards of different heights
 * packed into columns, the pinned ones in a group of their own on top, and
 * a "Take a note…" bar above them that opens into a title and a body (or a
 * checklist, or a photo) and saves itself when you click away.
 *
 * A card is the note at a glance: its colour behind it, the first photos,
 * the title, the body cut short or the unticked lines of its checklist, a
 * voice note to play where it is, and small chips for its labels, its list
 * and its reminders. Hovering (or focusing) a card shows its tools - pin,
 * colour, add a photo, archive, and a ⋯ for the rest - and a click anywhere
 * else on it opens the editor (NoteEditor.js).
 *
 * This file draws; TodoApp does every call and hands the answers back.
 */

/* ---------------------------------------------------------------- icons */

const Svg = ({ children, size = 18 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true" focusable="false">{children}</svg>
);
export const PinIcon = ({ on = false }) => (
  <Svg>
    <path d="M9 3h6l-1 6 4 4v1H6v-1l4-4z" fill={on ? 'currentColor' : 'none'} stroke="currentColor"
          strokeWidth="1.7" strokeLinejoin="round" />
    <path d="M12 14v7" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
  </Svg>
);
export const PaletteIcon = () => (
  <Svg>
    <path d="M12 3a9 9 0 100 18c1.2 0 1.8-.8 1.8-1.7 0-1.3-1.1-1.6-1.1-2.7 0-1 .8-1.6 1.8-1.6H17a4 4 0 004-4C21 6.6 17 3 12 3z"
          fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
    <circle cx="7.5" cy="11" r="1.3" fill="currentColor" />
    <circle cx="10" cy="7" r="1.3" fill="currentColor" />
    <circle cx="14.5" cy="7" r="1.3" fill="currentColor" />
  </Svg>
);
export const ImageIcon = () => (
  <Svg>
    <rect x="3.5" y="4.5" width="17" height="15" rx="2" fill="none" stroke="currentColor" strokeWidth="1.7" />
    <circle cx="9" cy="10" r="1.6" fill="currentColor" />
    <path d="M4 18l5-5 4 4 3-3 4 4" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
  </Svg>
);
export const ArchiveIcon = ({ back = false }) => (
  <Svg>
    <rect x="3.5" y="4" width="17" height="4.5" rx="1" fill="none" stroke="currentColor" strokeWidth="1.7" />
    <path d="M5 8.5V19a1 1 0 001 1h12a1 1 0 001-1V8.5" fill="none" stroke="currentColor" strokeWidth="1.7" />
    <path d={back ? 'M12 17v-5.5M9.5 14l2.5-2.5 2.5 2.5' : 'M12 11.5V17M9.5 14.5L12 17l2.5-2.5'} fill="none"
          stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
  </Svg>
);
export const ChecklistIcon = () => (
  <Svg>
    <rect x="3.5" y="3.5" width="17" height="17" rx="2.5" fill="none" stroke="currentColor" strokeWidth="1.7" />
    <path d="M7.5 12.2l3 3 6-6.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"
          strokeLinejoin="round" />
  </Svg>
);

/* -------------------------------------------------------------- palette */

/**
 * The colour of a note (or a to-do), as Keep offers it: a button that opens
 * a row of round swatches, "no colour" first. The swatches are the soft
 * tints a card is painted with, ringed in the colour's own swatch.
 */
export function Palette({ value, onPick, name, disabled = false }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); } };
    document.addEventListener('pointerdown', away);
    document.addEventListener('keydown', esc, true);
    return () => { document.removeEventListener('pointerdown', away); document.removeEventListener('keydown', esc, true); };
  }, [open]);
  return (
    <span className="tdn-palette" ref={ref}>
      <button type="button" className="tdn-tool" aria-label={`Colour of ${name}`} title="Background colour"
              aria-haspopup="true" aria-expanded={open} disabled={disabled}
              onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}>
        <PaletteIcon />
      </button>
      {open && (
        <span className="tdn-swatches" role="group" aria-label={`Colours for ${name}`}
              onClick={(e) => e.stopPropagation()}>
          {[null, ...COLORS].map((c) => (
            <button key={c || 'none'} type="button" className={`tdn-swatch ${swatch(c)}`}
                    aria-label={`Colour: ${colorName(c)}`} aria-pressed={(value || null) === c}
                    title={colorName(c)} onClick={() => { setOpen(false); onPick(c); }} />
          ))}
        </span>
      )}
    </span>
  );
}

/** A hidden file input behind an icon: "add a photo". */
export function ImagePick({ label, onFiles, disabled = false, children = null, className = 'tdn-tool' }) {
  const input = useRef(null);
  return (
    <>
      <button type="button" className={className} aria-label={label} title={label} disabled={disabled}
              onClick={(e) => { e.stopPropagation(); if (input.current) input.current.click(); }}>
        {children || <ImageIcon />}
      </button>
      <input ref={input} type="file" accept="image/*" multiple hidden aria-label={label} tabIndex={-1}
             onClick={(e) => e.stopPropagation()}
             onChange={(e) => {
               const files = [...(e.target.files || [])];
               e.target.value = '';
               if (files.length) onFiles(files);
             }} />
    </>
  );
}

/* ----------------------------------------------------------------- card */

/**
 * KEEP'S MASONRY, left to right. A grid of fixed-width columns whose rows
 * are a few pixels tall (.tdn-grid: grid-auto-rows); each card, when its
 * parent is such a grid, spans as many of those rows as its own height
 * needs. So the cards are placed in reading order - first row left to
 * right, then the next card into the shortest gap - with no empty column
 * at the right, the same number of columns in Pinned and in Others, and the
 * DOM (and a screen reader) in the order of the notes. A photo that loads,
 * a line ticked or a window resized changes a card's height, which the
 * ResizeObserver hears. Anywhere else (the board, the strip above a list)
 * the parent is not a .tdn-grid and nothing is done.
 */
function useMasonry(ref) {
  useLayoutEffect(() => {
    const el = ref.current;
    const grid = el && el.parentElement;
    if (!grid || !grid.classList.contains('tdn-grid')) return undefined;
    const fit = () => {
      const cs = getComputedStyle(grid);
      const unit = parseFloat(cs.gridAutoRows);
      // Rows that are not the small masonry rows: let the grid lay it out.
      if (!(unit > 0 && unit <= 16)) { el.style.gridRowEnd = ''; return; }
      const gap = parseFloat(cs.getPropertyValue('--tdn-gap')) || 0;
      const h = el.getBoundingClientRect().height;
      el.style.gridRowEnd = `span ${Math.max(1, Math.ceil((h + gap) / unit))}`;
    };
    fit();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => fit());
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
}

/**
 * One note in the grid. `actions` are TodoApp's: open, pin, color, images,
 * archive (or unarchive, in the archive), remove, copy, toTask, moveList,
 * labels, tick. `compact` is the board's version - no toolbar of its own,
 * the board puts its own ⋯ beside it.
 */
export function NoteCard({ item, actions, labelById, list = null, load, download, onError,
                           archived = false, busy = false, compact = false, extra = null }) {
  const imgs = imagesOf(item);
  const voices = audioOf(item);
  const tags = (item.labels || []).map((id) => labelById.get(id)).filter(Boolean);
  const rems = Array.isArray(item.reminders) ? item.reminders : [];
  const remText = rems.map(reminderLabel).filter(Boolean).join(', ');
  const hasList = Array.isArray(item.checklist);
  const prev = hasList ? checklistPreview(item.checklist) : null;
  const name = item.title ? `"${item.title}"` : 'this note';
  const self = useRef(null);
  useMasonry(self);
  const openIt = (e) => {
    if (e.target.closest('button, input, a, label, .tdn-tools, .tdn-voice, .tda-more')) return;
    actions.open(item);
  };

  return (
    <article className={`tdn-card ${swatch(item.color)}${item.color ? ' has-color' : ''}${item.pinned ? ' is-pinned' : ''}${compact ? ' is-compact' : ''}`}
             ref={self} data-note-id={item.id} data-color={item.color || ''} tabIndex={0}
             aria-label={item.title || 'Untitled note'}
             onClick={openIt}
             onKeyDown={(e) => { if (e.key === 'Enter' && e.target === e.currentTarget) { e.preventDefault(); actions.open(item); } }}>
      {imgs.length > 0 && (
        <div className={`tdn-card-imgs n-${Math.min(3, imgs.length)}`}>
          {imgs.slice(0, 3).map((a) => <NoteImage key={a.id} att={a} load={load} />)}
        </div>
      )}
      <div className="tdn-card-body">
        {item.pinned && !compact && <span className="tdn-pinned-mark" title="Pinned" aria-label="Pinned"><PinIcon on /></span>}
        {item.title && <h4 className="tdn-card-title">{item.title}</h4>}
        {!hasList && item.notes && <p className="tdn-card-text">{item.notes}</p>}
        {hasList && (
          <ul className="tdn-card-lines" aria-label="Checklist">
            {prev.shown.map((l) => (
              <li key={l.id} className="tdn-card-line" data-line={l.id}>
                <label onClick={(e) => e.stopPropagation()}>
                  <input type="checkbox" checked={false} disabled={busy || archived}
                         aria-label={`Tick "${l.text}"`}
                         onChange={() => actions.tick(item, l, true)} />
                  <span>{l.text}</span>
                </label>
              </li>
            ))}
            {prev.more > 0 && <li className="tdn-card-more" data-more={prev.more}>+{prev.more} more</li>}
            {prev.ticked > 0 && (
              <li className="tdn-card-ticked" data-ticked={prev.ticked}>
                {prev.ticked === 1 ? '1 ticked item' : `${prev.ticked} ticked items`}
              </li>
            )}
          </ul>
        )}
        {voices.length > 0 && (
          <NoteVoice att={voices[0]} load={load} download={download} onError={onError} />
        )}
        {(tags.length > 0 || list || rems.length > 0 || imgs.length > 0 || voices.length > 0) && (
          <div className="tdn-chips">
            {/* Where the photos and the player are not drawn (the strip
                above a list), a quiet count says they are there. */}
            {imgs.length > 0 && (
              <span className="tdn-chip is-media" title={imgs.length === 1 ? '1 photo' : `${imgs.length} photos`}>
                <NoteGlyph name="image" size={12} /><span className="tdn-chip-text">{imgs.length}</span>
              </span>
            )}
            {voices.length > 0 && (
              <span className="tdn-chip is-media" title={voices.length === 1 ? '1 voice note' : `${voices.length} voice notes`}>
                <NoteGlyph name="mic" size={12} /><span className="tdn-chip-text">{voices.length}</span>
              </span>
            )}
            {rems.length > 0 && (
              <span className="tdn-chip is-reminder" data-reminders={rems.length} title={`Reminders: ${remText}`}>
                <Icon name="bell" size={12} />
                <span className="tdn-chip-text">{reminderLabel(rems[0])}{rems.length > 1 ? ` +${rems.length - 1}` : ''}</span>
              </span>
            )}
            {tags.map((b) => (
              <span key={b.id} className="tdn-chip" data-label-chip={b.id} title={b.name}>
                <span className={`tda-dot ${swatch(b.color)}`} aria-hidden="true" /><span className="tdn-chip-text">{b.name}</span>
              </span>
            ))}
            {list && (
              <span className="tdn-chip is-list" data-list-chip={list.id} title={`In ${list.name}`}>
                <span className={`tda-dot ${list.is_inbox ? 'sw-none' : swatch(list.color)}`} aria-hidden="true" />
                <span className="tdn-chip-text">{list.name}</span>
              </span>
            )}
          </div>
        )}
      </div>
      {extra}
      {!compact && (
        <div className="tdn-tools" onClick={(e) => e.stopPropagation()}>
          {!archived && (
            <button type="button" className={`tdn-tool${item.pinned ? ' is-on' : ''}`} disabled={busy}
                    aria-pressed={Boolean(item.pinned)}
                    aria-label={item.pinned ? `Unpin ${name}` : `Pin ${name}`}
                    title={item.pinned ? 'Unpin' : 'Pin'} onClick={() => actions.pin(item)}>
              <PinIcon on={item.pinned} />
            </button>
          )}
          <Palette name={name} value={item.color} disabled={busy} onPick={(c) => actions.color(item, c)} />
          {!archived && <ImagePick label={`Add a photo to ${name}`} disabled={busy} onFiles={(f) => actions.images(item, f)} />}
          <button type="button" className="tdn-tool" disabled={busy}
                  aria-label={archived ? `Unarchive ${name}` : `Archive ${name}`}
                  title={archived ? 'Unarchive' : 'Archive'}
                  onClick={() => (archived ? actions.unarchive(item) : actions.archive(item))}>
            <ArchiveIcon back={archived} />
          </button>
          <MoreMenu label={`More for note ${name}`} disabled={busy} items={[
            { label: 'Delete note', danger: true, onClick: () => actions.remove(item) },
            !archived && { label: 'Make a copy', onClick: () => actions.copy(item) },
            // A note with no title is named from what is in it (the server's
            // titleFromNote) - it no longer has to be given one first.
            !archived && { label: 'Convert to to-do', onClick: () => actions.toTask(item) },
            !archived && actions.moveList && { label: 'Move to list…', onClick: () => actions.moveList(item) },
            !archived && actions.labels && { label: 'Labels…', onClick: () => actions.labels(item) }
          ]} />
        </div>
      )}
    </article>
  );
}

/**
 * The grid: "Pinned" first, then "Others" - headed only when there are both,
 * as Keep does. Each group is a .tdn-grid of the same fixed-width columns,
 * centred under the "Take a note…" bar, and the cards pack themselves into
 * it (useMasonry above), so both groups have the same number of columns
 * and line up one above the other.
 */
export function NotesGrid({ notes, render, empty = null }) {
  const pinned = notes.filter((t) => t.pinned);
  const others = notes.filter((t) => !t.pinned);
  if (!notes.length) return empty;
  const both = pinned.length > 0 && others.length > 0;
  return (
    <div className="tdn-groups">
      {pinned.length > 0 && (
        <section className="tdn-group is-pinned" aria-label="Pinned">
          <h3 className="tdn-group-head">Pinned</h3>
          <div className="tdn-grid">{pinned.map(render)}</div>
        </section>
      )}
      {others.length > 0 && (
        <section className="tdn-group is-others" aria-label="Others">
          {both && <h3 className="tdn-group-head">Others</h3>}
          <div className="tdn-grid">{others.map(render)}</div>
        </section>
      )}
    </div>
  );
}

/**
 * A list's (or a label's) notes, ABOVE its to-dos: a strip, not the grid.
 * The to-dos are what the list is for, so its notes take one row of small
 * cards (no photo, no player - a count says they are there; CSS cuts each
 * card at 120px) that scrolls sideways, pinned ones first, under a
 * "Notes 3" heading that folds the row away. Folded or not is remembered
 * per list, on this device only.
 */
export function NotesStrip({ id, title, notes, render }) {
  const key = `tdn-strip-folded:${id}`;
  const [folded, setFolded] = useState(() => {
    try { return window.localStorage.getItem(key) === '1'; } catch (e) { return false; }
  });
  const toggle = () => {
    const next = !folded;
    setFolded(next);
    try { window.localStorage.setItem(key, next ? '1' : '0'); } catch (e) { /* private window */ }
  };
  const ordered = [...notes.filter((t) => t.pinned), ...notes.filter((t) => !t.pinned)];
  return (
    <section className={`tdn-strip${folded ? ' is-folded' : ''}`} aria-label={`Notes in ${title}`}>
      <h3 className="tdn-strip-title">
        <button type="button" className="tdn-strip-head" aria-expanded={!folded} onClick={toggle}>
          <Icon name={folded ? 'chevronRight' : 'chevronDown'} size={14} />
          <span>Notes</span><span className="tdn-strip-n">{notes.length}</span>
        </button>
      </h3>
      {!folded && <div className="tdn-strip-row">{ordered.map(render)}</div>}
    </section>
  );
}

/* --------------------------------------------------------- the lines editor */

/**
 * Checklist lines typed before the note exists - the bar's "New list". Each
 * line is a checkbox and a field: Enter makes the next line, Backspace on an
 * empty one takes it away and goes back up. The last, empty field is where
 * a new line starts.
 */
function DraftLines({ lines, setLines }) {
  const refs = useRef({});
  // Opened as "New list": straight into its first line.
  const [focus, setFocus] = useState(() => (lines.length === 1 && !lines[0].text ? lines[0].id : null));
  useEffect(() => {
    if (!focus) return;
    const el = refs.current[focus];
    if (el) el.focus();
    setFocus(null);
  }, [focus, lines]);
  const at = (id) => lines.findIndex((l) => l.id === id);
  return (
    <ul className="tdn-lines is-draft" aria-label="Checklist">
      {lines.map((l) => (
        <li key={l.id} className={`tdn-line${l.done ? ' is-done' : ''}`}>
          <input type="checkbox" checked={l.done} aria-label={`Tick "${l.text || 'this line'}"`}
                 onChange={(e) => setLines(lines.map((x) => (x.id === l.id ? { ...x, done: e.target.checked } : x)))} />
          <input type="text" className="tdn-line-text" value={l.text} maxLength={500}
                 ref={(el) => { refs.current[l.id] = el; }} aria-label="List item"
                 onChange={(e) => setLines(lines.map((x) => (x.id === l.id ? { ...x, text: e.target.value } : x)))}
                 onKeyDown={(e) => {
                   const i = at(l.id);
                   if (e.key === 'Enter') {
                     e.preventDefault();
                     if (lines.length >= MAX_LINES) return;
                     const n = { id: lineId(), text: '', done: false };
                     setLines([...lines.slice(0, i + 1), n, ...lines.slice(i + 1)]);
                     setFocus(n.id);
                   } else if (e.key === 'Backspace' && !l.text && lines.length > 1) {
                     e.preventDefault();
                     setLines(lines.filter((x) => x.id !== l.id));
                     setFocus((lines[i - 1] || lines[i + 1]).id);
                   }
                 }} />
        </li>
      ))}
    </ul>
  );
}

/* ------------------------------------------------------------ take a note */

const EMPTY_NOTE = { title: '', body: '', lines: null, color: null, pinned: false, files: [] };

/**
 * "Take a note…", Keep's bar. Closed, it is one line with two shortcuts
 * beside it: a new checklist and a new note with a photo. Open, a title and
 * a body (or the lines). A click anywhere else closes it - and SAVES it if
 * anything was written, as Keep does; an empty one is simply let go. Escape
 * and Close do the same.
 */
export function TakeNote({ onCreate, busy = false }) {
  const [open, setOpen] = useState(false);
  const [v, setV] = useState(EMPTY_NOTE);
  const ref = useRef(null);
  const bodyRef = useRef(null);
  const [previews, setPreviews] = useState([]);
  const vRef = useRef(v);
  vRef.current = v;

  useEffect(() => {
    const urls = v.files.map((f) => URL.createObjectURL(f));
    setPreviews(urls);
    return () => urls.forEach((u) => URL.revokeObjectURL(u));
  }, [v.files]);

  const close = () => {
    const n = vRef.current;
    const lines = Array.isArray(n.lines) ? n.lines.filter((l) => l.text.trim()) : null;
    const something = n.title.trim() || n.body.trim() || (lines && lines.length) || n.files.length;
    setOpen(false);
    setV(EMPTY_NOTE);
    if (!something) return;
    onCreate({
      title: n.title.trim(),
      notes: lines ? null : (n.body.trim() || null),
      checklist: lines && lines.length ? lines.map((l) => ({ id: l.id, text: l.text.trim(), done: l.done })) : null,
      color: n.color,
      pinned: n.pinned
    }, n.files);
  };
  const closeRef = useRef(close);
  closeRef.current = close;

  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => {
      if (!ref.current || ref.current.contains(e.target)) return;
      // A palette or menu opened from the bar lives inside it; anything
      // else on the page is "somewhere else".
      closeRef.current();
    };
    document.addEventListener('pointerdown', away);
    return () => document.removeEventListener('pointerdown', away);
  }, [open]);

  const start = (patch = {}) => {
    setV((prev) => ({ ...prev, ...patch }));
    setOpen(true);
  };

  if (!open) {
    return (
      <div className="tdn-take card" ref={ref}>
        <input type="text" className="tdn-take-input" placeholder="Take a note…" aria-label="Take a note"
               readOnly onFocus={() => start()} onClick={() => start()} />
        <button type="button" className="tdn-tool" aria-label="New list" title="New list"
                onClick={() => start({ lines: [{ id: lineId(), text: '', done: false }] })}>
          <ChecklistIcon />
        </button>
        <ImagePick label="New note with image" onFiles={(files) => start({ files })} />
      </div>
    );
  }

  return (
    <div className={`tdn-take card is-open ${swatch(v.color)}${v.color ? ' has-color' : ''}`} ref={ref}
         onKeyDown={(e) => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); close(); } }}>
      {previews.length > 0 && (
        <div className={`tdn-card-imgs n-${Math.min(3, previews.length)}`}>
          {previews.slice(0, 3).map((u, i) => <img key={u} className="tdn-img" src={u} alt={v.files[i]?.name || 'Photo'} />)}
        </div>
      )}
      <div className="tdn-take-head">
        <input type="text" className="tdn-take-title" placeholder="Title" aria-label="Note title" maxLength={300}
               value={v.title} autoFocus={!v.lines}
               onChange={(e) => setV({ ...v, title: e.target.value })}
               onKeyDown={(e) => {
                 if (e.key === 'Enter') { e.preventDefault(); if (bodyRef.current) bodyRef.current.focus(); }
               }} />
        <button type="button" className={`tdn-tool${v.pinned ? ' is-on' : ''}`} aria-pressed={v.pinned}
                aria-label={v.pinned ? 'Unpin the new note' : 'Pin the new note'}
                onClick={() => setV({ ...v, pinned: !v.pinned })}><PinIcon on={v.pinned} /></button>
      </div>
      {Array.isArray(v.lines) ? (
        <DraftLines lines={v.lines} setLines={(lines) => setV((prev) => ({ ...prev, lines }))} />
      ) : (
        <textarea ref={bodyRef} className="tdn-take-body" placeholder="Take a note…" aria-label="Note"
                  rows={3} maxLength={20000} value={v.body} autoFocus={Boolean(v.title)}
                  onChange={(e) => setV({ ...v, body: e.target.value })} />
      )}
      <div className="tdn-take-foot">
        <Palette name="the new note" value={v.color} onPick={(c) => setV({ ...v, color: c })} />
        <ImagePick label="Add a photo to the new note" onFiles={(files) => setV({ ...v, files: [...v.files, ...files] })} />
        <button type="button" className="tdn-tool" aria-pressed={Array.isArray(v.lines)}
                aria-label={Array.isArray(v.lines) ? 'Hide checkboxes' : 'Show checkboxes'}
                title={Array.isArray(v.lines) ? 'Hide checkboxes' : 'Show checkboxes'}
                onClick={() => setV(Array.isArray(v.lines)
                  ? { ...v, lines: null, body: v.lines.map((l) => l.text).filter((s) => s.trim()).join('\n') }
                  : { ...v, body: '', lines: v.body.split(/\r?\n/).filter((s) => s.trim()).map((text) => ({ id: lineId(), text, done: false })).concat(v.body.trim() ? [] : [{ id: lineId(), text: '', done: false }]) })}>
          <ChecklistIcon />
        </button>
        <span className="tdn-grow" />
        <button type="button" className="btn btn-secondary btn-small tdn-close" disabled={busy} onClick={close}>Close</button>
      </div>
    </div>
  );
}
